import { ArrowSmRightIcon } from "@heroicons/react/outline";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { env } from "~/config/env";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { useBreakpoint } from "~/design-system/hooks";
import { Icon } from "~/design-system/Icon";
import { ClovisIcon } from "~/design-system/Icon/icons/outline/ClovisIcon";
import { Text } from "~/design-system/Text";
import { createComponentHook } from "~/types";
import type { LanguageDropdownProps } from "../LanguageDropdown/LanguageDropdown";
import { LanguageDropdown } from "../LanguageDropdown/LanguageDropdown";

const useRightsHeader = createComponentHook((props: LanguageDropdownProps) => {
  const { breakpoint } = useBreakpoint();
  const { t } = useTranslation();

  return {
    actions: { setLanguage: props.setLanguage },
    state: { breakpoint, language: props.language },
    t,
  };
});

const RightsHeader = (props: LanguageDropdownProps) => {
  const { actions, state, t } = useRightsHeader(props);
  const navigate = useNavigate();

  const isMobile = state.breakpoint === "mobile";

  /* if the previous route was inside the app, go back to the app, otherwise go to login */
  const backButton = document.referrer?.includes(
    env.VITE_AUTH0_DOMAIN?.replace("auth.", "")
  )
    ? {
        content: isMobile
          ? t(
              "screens.Rights.components.RightsHeader.backButtonClovisMobile",
              "Back"
            )
          : t(
              "screens.Rights.components.RightsHeader.backButtonClovis",
              "Back to Users Rights"
            ),
        onClick: () => navigate(-1),
      }
    : {
        content: isMobile
          ? t(
              "screens.Rights.components.RightsHeader.backButtonLoginClovisMobile",
              "Login"
            )
          : t(
              "screens.Rights.components.RightsHeader.backButtonLoginClovis",
              "Login to Clovis"
            ),
        onClick: () => {
          window.location.href = env.VITE_APP_BASE_URL;
        },
      };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="spaceBetween"
        paddingX="medium"
        paddingY="small"
        css={{
          // Reduced height for thinner header
          borderBottom: "1px solid $colors$neutral200",
          flexDirection: "row",
          gap: "$space$3",
          paddingBottom: "$space$3",
          paddingTop: "$space$3",
        }}
      >
        {/* Logo and Title Section */}
        <Box
          display="flex"
          alignItems="center"
          css={{
            gap: "$space$3",
            justifyContent: "flex-start",
            width: "auto",
          }}
        >
          <Icon
            size="medium"
            color="clovisNavy"
            label="Clovis logo"
            css={{ flexShrink: 0 }}
          >
            <ClovisIcon />
          </Icon>
          <Text
            css={{
              color: "$colors$slate900",
              fontSize: "$xl",
              fontWeight: "$medium",
            }}
            data-clovis-action-id="rights-header-title"
          >
            Clovis
          </Text>
          {!isMobile && (
            <Text
              css={{
                color: "$colors$neutral600",
                fontSize: "$lg",
                marginLeft: "$space$2",
              }}
            >
              {t(
                "screens.Rights.components.RightsHeader.title",
                "Clovis.app Users Rights"
              )}
            </Text>
          )}
        </Box>

        {/* Right Section - Buttons */}
        <Box
          display="flex"
          alignItems="center"
          css={{
            gap: "$space$3",
            justifyContent: "flex-end",
            width: "auto",
          }}
        >
          <LanguageDropdown
            language={state.language}
            setLanguage={actions.setLanguage}
            showTextOnMobile={false}
            size={isMobile ? "small" : "standard"}
          />
          <Button
            size={isMobile ? "small" : "standard"}
            variant="ghost"
            trailingIcon={<ArrowSmRightIcon />}
            dataClovisActionId="rights-go-back-toclovis"
            onClick={backButton.onClick}
          >
            {backButton.content}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export { RightsHeader };
